/**
 * Tiny typed fetch client. Cookies (HttpOnly) carry the access JWT for
 * dev (Vite proxy) and same-site prod. For non-cookie clients, set
 * Authorization at instantiation.
 *
 * Throws an `ApiError` (with `.status`) on non-2xx so TanStack Query can
 * decide retry behavior.
 */
/** Exported so routes that must bypass `api` (blob downloads, uploads)
 *  do not each re-derive it — and each re-trip the same tsc complaint. */
export const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  json?: unknown;
  body?: BodyInit;
  query?: Record<string, string | number | boolean | undefined | null>;
}

// One in-flight refresh shared across concurrent callers, so a burst of 401s
// triggers a single POST /auth/refresh rather than a stampede.
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchange the long-lived (30-day) HttpOnly refresh cookie for a fresh access
 * token. Returns true if the server re-issued an access token. Uses raw fetch
 * so it never recurses through the 401 interceptor below.
 */
function tryRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    const url = new URL(BASE + "/auth/refresh", window.location.origin)
      .toString()
      .replace(window.location.origin, "");
    refreshInFlight = fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: "{}",
      credentials: "include",
    })
      .then((r) => r.ok)
      .catch(() => false)
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

/** A Zod issue as the API returns it — only the parts worth showing. */
type ApiIssue = { path?: (string | number)[]; message?: string };

/**
 * Turn an error payload into something a toast can show. A rejected body comes
 * back as `{error: "validation_error", issues: [...]}`, and that bare code tells
 * the editor nothing about what to fix, so prefer the per-field messages.
 */
function errorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== "object") return fallback;
  const { error, issues } = payload as { error?: unknown; issues?: unknown };
  if (Array.isArray(issues)) {
    const lines = (issues as ApiIssue[])
      .map((i) => {
        const field = (i.path ?? []).filter((p) => typeof p === "string").join(".");
        const message = i.message ?? "is invalid";
        return field ? `${field}: ${message}` : message;
      })
      .filter(Boolean);
    if (lines.length) return lines.join("; ");
  }
  return error ? String(error) : fallback;
}

async function call<T>(path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
  const url = new URL(BASE + path, window.location.origin);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v == null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  const headers = new Headers(opts.headers);
  let body: BodyInit | undefined = opts.body;
  if (opts.json !== undefined) {
    headers.set("content-type", "application/json");
    body = JSON.stringify(opts.json);
  }
  const r = await fetch(url.toString().replace(window.location.origin, ""), {
    ...opts,
    headers,
    body,
    credentials: "include",
  });

  // Access token expired (15 min TTL): silently refresh once and retry the
  // original request, so the session lasts the full refresh-token lifetime.
  // Skip for the auth endpoints themselves to avoid loops / bad-credential retries.
  if (
    r.status === 401 &&
    !retried &&
    !path.startsWith("/auth/refresh") &&
    !path.startsWith("/auth/login")
  ) {
    const refreshed = await tryRefresh();
    if (refreshed) return call<T>(path, opts, true);
  }

  const text = await r.text();
  let payload: unknown = null;
  try { payload = text ? JSON.parse(text) : null; } catch { payload = text; }
  if (!r.ok) throw new ApiError(errorMessage(payload, r.statusText), r.status, payload);
  return payload as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions["query"]) => call<T>(path, { method: "GET", query }),
  post: <T>(path: string, json?: unknown) => call<T>(path, { method: "POST", json }),
  patch: <T>(path: string, json?: unknown) => call<T>(path, { method: "PATCH", json }),
  put: <T>(path: string, json?: unknown) => call<T>(path, { method: "PUT", json }),
  del: <T>(path: string) => call<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, formData: FormData) => call<T>(path, { method: "POST", body: formData }),
};

export type Me = {
  id: number;
  email: string;
  name: string;
  avatar_r2_key: string | null;
  last_login_at: number | null;
  roles: string[];
  permissions: string[];
};
